import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { announceReactionReply } from "../utils/reaction_reply"

// Emoji reactions on a chat bubble (merged person thread, standalone connection
// chat, or standalone channel chat). Wraps one bubble's chip row. The "add
// reaction" picker itself lives outside this controller now — it's the shared
// reaction picker (shared/_reaction_picker.html.slim, emoji_picker_controller.js)
// — so this controller only ever toggles existing chips. Any interactive
// surface passes a urlValue — the internal-DM connection endpoint or the
// channel-message endpoint; a read-only surface omits it, so those bubbles
// render static chips with no toggle.
//
// Toggling round-trips to ConnectionMessageReactionsController /
// ChannelMessageReactionsController / DatingMessageReactionsController; on the
// two cable-backed surfaces the resulting chip row is Turbo-replaced by the
// matching broadcast (PersonBroadcast / ChatBroadcast) over the live stream,
// so those responses come back 204 empty and this is a no-op fetch-and-discard
// — we just POST/DELETE and let the broadcast reconcile. The dating match chat
// has no broadcast (it's polled — see peer_chat_controller.js), so its
// controller instead returns the replacement chip row as a turbo-stream body
// directly; we apply that ourselves the same way
// emoji_picker_controller.js#_send already does for its own broadcast-less
// endpoints. Own chips that can't be un-reacted (Unipile channel messages)
// render as static spans server-side, so toggleChip only ever fires where a
// change is possible.
//
// Relay rows (shared/_chat_reactions `relay: true` — the cable rooms) have no
// url: a tap re-raises "emoji-picker:picked" for the room's controller, and
// the viewer's own chips are marked here, since the row was rendered once for
// everyone.
export default class extends Controller {
    static values = { url: String }

    connect() {
        if (this.urlValue) return
        const me = this.element.closest("[data-reactor-name]")?.dataset.reactorName
        if (!me) return
        this.element.querySelectorAll(".dm-reaction-chip[data-reactors]").forEach((chip) => {
            let reactors = []
            try { reactors = JSON.parse(chip.dataset.reactors) } catch (_error) { /* malformed */ }
            chip.classList.toggle("is-mine", reactors.includes(me))
        })
    }

    // An existing chip: remove it if it's already mine, otherwise add mine.
    toggleChip(e) {
        const chip = e.currentTarget
        const emoji = chip.dataset.emoji
        if (!emoji) return
        if (!this.urlValue) {
            chip.dispatchEvent(new CustomEvent("emoji-picker:picked", { bubbles: true, detail: { emoji } }))
            return
        }
        this._send(emoji, chip.classList.contains("is-mine") ? "DELETE" : "POST")
    }

    _send(emoji, method) {
        if (!this.urlValue) return
        const body = new FormData()
        body.set("emoji", emoji)
        fetch(this.urlValue, {
            method,
            headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": csrfToken() },
            body
        })
            .then((response) => {
                announceReactionReply(response)
                return response.text()
            })
            .then((html) => {
                if (html && window.Turbo) window.Turbo.renderStreamMessage(html)
            })
            .catch(() => {})
    }
}
